/**
 * Client wrappers for the handheld ingest callables (suite-io/functions/src/
 * handlers/handheld/). Same Firebase project — no cross-project bridge.
 * Errors propagate as Firebase HttpsError; callers read err.code (e.g.
 * "permission-denied", "already-exists", "failed-precondition").
 *
 * Mirrors src/lib/ingestCallables.js, including the undefined-omit guard: the
 * callable client serializes undefined → null on the wire, and the backend
 * schema's .default()/.optional() accepts undefined but rejects null, so only
 * include a field when it was actually provided.
 */
import { callable } from "../firebase.js";

const call = async (name, payload) => {
  const fn = callable(name);
  const result = await fn(payload);
  return result.data;
};

export const handheld = {
  /**
   * Create a subject. clientTempId is REQUIRED and must be stable across retries
   * of the same logical add (idempotency — a retry returns the same {id,number},
   * no second doc). Returns { id, number }.
   */
  addSubject: ({ jobId, displayName, category, capturedBy, clientTempId, presetIds }) =>
    call("addSubject", {
      jobId,
      displayName,
      category,
      clientTempId,
      ...(capturedBy !== undefined ? { capturedBy } : {}),
      ...(presetIds !== undefined ? { presetIds } : {}),
    }),

  setSubjectPresets: ({ jobId, subjectId, presetIds }) =>
    call("setSubjectPresets", { jobId, subjectId, presetIds }),

  /** Rename a subject (job-wide-unique guarded; appends displayNameHistory). */
  renameSubject: ({ jobId, subjectId, displayName }) =>
    call("renameSubject", { jobId, subjectId, displayName }),

  /** Change a subject's category. */
  setCategory: ({ jobId, subjectId, category }) =>
    call("setCategory", { jobId, subjectId, category }),

  /** Erase an EMPTY subject (no raw/projects/exports); frees its name. */
  deleteSubject: ({ jobId, subjectId }) =>
    call("deleteSubject", { jobId, subjectId }),

  /** Persist a subject's reference-photo Storage PATH (after the client upload). */
  setReferencePhoto: ({ jobId, subjectId, storagePath }) =>
    call("setReferencePhoto", { jobId, subjectId, storagePath }),

  /** Raw Verify: file a raw set onto a subject (auto-folder); returns { folder }. */
  attachRawSet: ({ jobId, subjectId, code, device, suffix, attempt, expectedFolder }) =>
    call("attachRawSet", {
      jobId, subjectId, code,
      ...(device !== undefined ? { device } : {}),
      ...(suffix !== undefined ? { suffix } : {}),
      ...(attempt !== undefined ? { attempt } : {}),
      ...(expectedFolder !== undefined ? { expectedFolder } : {}),
    }),

  /**
   * Create a project (mints the per-job serial). clientTempId is REQUIRED + must
   * be stable across retries of the same logical create. Returns { id, number }.
   */
  createProject: ({ jobId, displayName, software, tethered, clientTempId }) =>
    call("createProject", {
      jobId, software, clientTempId,
      ...(displayName !== undefined ? { displayName } : {}),
      ...(tethered !== undefined ? { tethered } : {}),
    }),

  /** Link a subject into a project — ONE atomic call writes both sides. */
  linkSubjectToProject: ({ jobId, subjectId, projectId }) =>
    call("linkSubjectToProject", { jobId, subjectId, projectId }),

  /** Unlink (rollback partner of link) — removes both sides atomically. */
  unlinkSubjectFromProject: ({ jobId, subjectId, projectId, clearCapturedByCode }) =>
    call("unlinkSubjectFromProject", {
      jobId, subjectId, projectId,
      ...(clearCapturedByCode !== undefined ? { clearCapturedByCode } : {}),
    }),

  /** Activate an Empty subject as a tethered scan + link it into a project. */
  activateTethered: ({ jobId, subjectId, code, projectId }) =>
    call("activateTethered", { jobId, subjectId, code, projectId }),

  /** Delete an EMPTY project (rollback of a half-failed Ingest-project commit). */
  deleteProject: ({ jobId, projectId }) =>
    call("deleteProject", { jobId, projectId }),

  /**
   * Export Verify: attach (or refresh) an export under a subject. Dedupes on
   * code+suffix+lineage. meta = manual file specs (optional now; the bridge fills
   * the same shape in Seg 4). Returns { id, exportId }.
   */
  attachExport: ({ jobId, subjectId, code, suffix, lineage, meta, attempt }) =>
    call("attachExport", {
      jobId, subjectId, code,
      ...(suffix !== undefined ? { suffix } : {}),
      ...(lineage !== undefined ? { lineage } : {}),
      ...(meta !== undefined ? { meta } : {}),
      ...(attempt !== undefined ? { attempt } : {}),
    }),

  /** Segment 4: ask a studio computer's bridge to read an export's file again. */
  requestExportVerify: ({ jobId, subjectId, exportId }) =>
    call("requestExportVerify", { jobId, subjectId, exportId }),

  /** Segment 4 fallback: type an export's specs by hand (cancels an open read). */
  enterExportSpecs: ({ jobId, subjectId, exportId, meta }) =>
    call("enterExportSpecs", { jobId, subjectId, exportId, meta }),
};

/** Idempotency key for a single logical create; reuse the same value on retry. */
export const newClientTempId = () => `tmp_${crypto.randomUUID()}`;
